<template>
  <v-card :width="$isMobile ? (windowInnerWidth * 0.98) - 80 : 734" class="loading-wrapper">
    <div style="width: 100%" :class="[$isMobile ? 'pl-3' : 'pl-6', 'd-flex align-center justify-space-between pr-4']">
      <div style="color: #8f91a8">
        <v-progress-circular
          class="mr-1"
          :size="22"
          indeterminate
          color="primary"
        ></v-progress-circular>
        <span class="thinking-prompt" v-if="startProgressBar">{{ thinkingPrompt }}...</span>
      </div>
      <div>
      </div>
    </div>
    <v-card-text v-if="startProgressBar" class="loading-container pb-0">
      <!-- 第一条加载条 -->
      <div class="bar-item">
        <div ref="bar1" class="bar" :style="{ height: height, marginRight: gap }">
          <div
            class="bar-fill"
            :style="{
            width: bar1Progress + '%',
            backgroundColor: color,
          }"
          ></div>
        </div>
        <div :class="['mt-2', promptClassName]" :style="{color: currentBar >= 1 ?'#2C2C36' : '#8F91AB'}">
          <span>{{ bar1Prompt }}</span>
        </div>
      </div>

      <!-- 第二条加载条 -->
      <div class="bar-item">
        <div ref="bar2" class="bar" :style="{ height: height, marginRight: gap }">
          <div
            class="bar-fill"
            :style="{
            width: bar2Progress + '%',
            backgroundColor: color,
          }"
          ></div>
        </div>
        <div :class="['mt-2', promptClassName]" :style="{color: currentBar >= 2 ?'#2C2C36' : '#8F91AB'}">
          <span>{{ bar2Prompt }}</span>
        </div>
      </div>

      <!-- 第三条加载条 -->
      <div class="bar-item">
        <div ref="bar3" class="bar" :style="{ height: height }">
          <div
            class="bar-fill"
            :style="{
            width: bar3Progress + '%',
            backgroundColor: color,
          }"
          ></div>
        </div>
        <div :class="[promptClassName, 'mt-2 d-flex align-center justify-space-between']">
          <div :style="{color: (currentBar >= 3 || isCompleted) ?'#2C2C36' : '#8F91AB'}">{{ bar3Prompt }}</div>
          <div :style="{color: isCompleted ?'#2C2C36' : '#8F91AB'}">{{ completePrompt }}</div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'StrictSequentialLoading',
  props: {
    color: {
      type: String,
      default: '#ABA8FF',
    },
    height: {
      type: String,
      default: '8px',
    },
    gap: {
      type: String,
      default: '6px',
    },
    bar1Duration: {
      type: Number,
      default: 3000,
    },
    bar2Duration: {
      type: Number,
      default: 5000,
    },
    bar3Duration: {
      type: Number,
      default: 5000, // 第三条加载条持续时间，默认2秒
    },
    bar3Max: {
      type: Number,
      default: 89,
    },
    showCompletionText: {
      type: Boolean,
      default: true,
    },
    language: {
      type: String,
      default: 'chinese',
    },
    searchComplete: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    currentBar: {
      handler(val) {
        if (this.language === 'chinese') {
          this.thinkingPrompt = '正在' + this[`bar${val}Prompt`]
        } else {
          if (val === 1) {
            this.thinkingPrompt = 'Understanding the question'
          } else if (val === 2) {
            this.thinkingPrompt = 'Searching for answers'
          } else if (val === 3) {
            this.thinkingPrompt = 'Generating answers'
          }
        }
      },
      immediate: true,
    },
    searchComplete: {
      handler(val) {
        console.log('searchComplete:', val)
        if (val) {
          this.complete()
        }
      },
    },
    language: {
      handler(val) {
        console.log('language#watch', val)
      },
    },
  },
  computed: {
    windowInnerWidth() {
      return window.innerWidth
    },
    bar1Prompt() {
      return this.language === 'chinese' ? '理解问题' : (this.$isMobile ? 'Understand' : 'Understand Questions')
    },
    bar2Prompt() {
      return this.language === 'chinese' ? '搜索答案' : (this.$isMobile ? 'Search' : 'Search for answers')
    },
    bar3Prompt() {
      return this.language === 'chinese' ? '生成答案' : (this.$isMobile ? 'Generate' : 'Generate answers')
    },
    completePrompt() {
      return this.language === 'chinese' ? '完成' : 'Completed'
    },
    promptClassName() {
      return this.language === 'chinese' ? 'prompt' : 'english-prompt'
    },
  },
  data() {
    return {
      isCompleted: false,
      bar1Progress: 0,
      bar2Progress: 0,
      bar3Progress: 0,
      currentBar: 1,
      animationFrame: null,
      pulseAnimationFrame: null,
      startTime: null,
      startProgressBar: false,
      // bar1Prompt: this.language === 'chinese' ? '理解问题' : 'Comprehension Questions',
      // bar2Prompt: this.language === 'chinese' ? '搜索答案' : 'Search for answers',
      // bar3Prompt: this.language === 'chinese' ? '生成答案' : 'Generate answers',
      // completePrompt: this.language === 'chinese' ? '完成' : 'Completed',
      thinkingPrompt: '',
    }
  },
  methods: {
    startStrictAnimation() {
      this.currentBar = 1
      this.startTime = performance.now()
      this.animateBar(1, 100, this.bar1Duration)
        .then(() => {
          if (this.isCompleted) return
          this.currentBar = 2
          return this.animateBar(2, 100, this.bar2Duration)
        })
        .then(() => {
          if (this.isCompleted) return
          this.currentBar = 3
          return this.animateBar(3, this.bar3Max, this.bar3Duration)
        })
        .then(() => {
          if (this.isCompleted) return
          this.startBar3Pulse()
        })
    },

    animateBar(barNumber, targetProgress, duration) {
      return new Promise(resolve => {
        const startProgress = this[`bar${barNumber}Progress`]
        const startTime = performance.now()

        const animate = timestamp => {
          const elapsed = timestamp - startTime
          const progress = Math.min(elapsed / duration, 1)
          this[`bar${barNumber}Progress`] = startProgress + (targetProgress - startProgress) * progress

          if (progress < 1) {
            this.animationFrame = requestAnimationFrame(animate)
          } else {
            this[`bar${barNumber}Progress`] = targetProgress
            resolve()
          }
        }

        this.animationFrame = requestAnimationFrame(animate)
      })
    },

    startBar3Pulse() {
      this.bar3Progress = this.bar3Max
      let lastFrame = performance.now()

      const pulse = timestamp => {
        if (this.isCompleted) return

        const delta = timestamp - lastFrame
        if (delta > 100) {
          const minProgress = this.bar3Max - 0.2
          this.bar3Progress = this.bar3Progress <= minProgress ? this.bar3Max : this.bar3Progress - 0.04
          lastFrame = timestamp
        }
        this.pulseAnimationFrame = requestAnimationFrame(pulse)
      }

      this.pulseAnimationFrame = requestAnimationFrame(pulse)
    },

    async complete() {
      this.isCompleted = true
      cancelAnimationFrame(this.animationFrame)
      cancelAnimationFrame(this.pulseAnimationFrame)
      const completeChain = [
        { bar: 1, condition: this.currentBar === 1 },
        { bar: 2, condition: this.currentBar <= 2 },
        { bar: 3, condition: true },
      ]

      for (const { bar, condition } of completeChain) {
        if (condition && this[`bar${bar}Progress`] < 100) {
          await this.animateBar(bar, 100, 200)
        }
      }

      this.$emit('completed')
    },

    reset() {
      this.isCompleted = false
      cancelAnimationFrame(this.animationFrame)
      cancelAnimationFrame(this.pulseAnimationFrame)
      this.bar1Progress = 0
      this.bar2Progress = 0
      this.bar3Progress = 0
      this.currentBar = 1
      this.$nextTick(() => this.startStrictAnimation())
      this.$emit('reset')
    },
  },
  mounted() {
    setTimeout(() => this.startProgressBar = true, 2000)
    this.$nextTick(() => this.startStrictAnimation())
  },
  beforeUnmount() {
    cancelAnimationFrame(this.animationFrame)
    cancelAnimationFrame(this.pulseAnimationFrame)
  },
}
</script>

<style scoped>
.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-radius: 12px;
}

.loading-container {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
}

.bar {
  flex: 1;
  background-color: #EDEFF5;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.bar-item {
  width: 33.3%;
}

.bar-fill {
  height: 100%;
  border-radius: 4px 0 0 4px;
  position: relative;
}

.bar-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 50px;
  height: 100%;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0));
  animation: shine 2s infinite;
}

@keyframes shine {
  0% {
    transform: translateX(-100px);
  }
  100% {
    transform: translateX(500px);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 767px) {
  .thinking-prompt {
    font-size: 13px;
  }

  .prompt {
    font-size: 11px;
  }

  .english-prompt {
    font-size: 10px;
  }

  .loading-container {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding-left: 5px;
    padding-right: 5px;
  }
}
</style>
